import { supabase } from './supabaseClient';
import { LinkedProjectConfig, ServiceItem } from '../types';
import { bestandteilFelderAusZeile } from './projectLv';
import {
    ABGESCHLOSSEN,
    BillingLogEntry,
    UnbookedSentReportRow,
    istAusgeglichen,
    istTagGedeckt,
} from './unbookedReportsLogic';
import { TAGESPAUSCHALE, billingModeAt } from './linkedBillingMode';
import { PROJECT_LINK_COLUMNS, ProjectLinkColumns, linkConfigFromRow } from './projectLink';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WELCHE PROJEKTE MELDEN NICHT MEHR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ein Projekt wird still, wenn es IM ARCHIV liegt (`status = 'finished'`) UND
 * keine Rechnung mehr zu schreiben ist. Die Regel selbst steht in
 * `unbookedReportsLogic` (`istAusgeglichen`); hier wird nur beschafft, was sie
 * braucht — und das steht in zwei Tabellen, nicht in einer.
 *
 * ─── Warum das eine eigene Abfrage ist ─────────────────────────────────────
 *
 * Der Nachtlauf kann die Frage beantworten, weil er die Projekte samt
 * Positionen ohnehin geladen hat. Die OBERFLAECHE kann es nicht: weder die
 * Sicht `v_unbooked_sent_reports` noch die Tabelle `integrity_findings` fuehrt
 * die Positionen mit.
 *
 * Und die Oberflaeche MUSS die Frage selbst stellen. Der Waechter filtert beim
 * ERZEUGEN; seine gespeicherten Zeilen bekommen erst im naechsten Nachtlauf
 * `resolved_at`. Bis dahin — bis zu einem Tag — zeigte die Verwaltung Befunde
 * zu Projekten, die laengst abgeschlossen und ausgeglichen sind. Genau das ist
 * bei Projekt 4959 A aufgefallen: abgeschlossen, Position 38 von 38 gebucht,
 * und trotzdem sechs Meldungen in der Liste.
 *
 * ─── Die sichere Richtung ──────────────────────────────────────────────────
 *
 * Jeder Fehler und jede Wissensluecke fuehrt zu MELDEN, nie zu Schweigen:
 * schlaegt eine der beiden Abfragen fehl, kommt eine leere Menge zurueck und
 * es bleibt alles stehen. Eine Zeile zu viel kostet einen Blick, eine
 * verschwiegene Forderung kostet Geld.
 */

/** PostgREST kappt eine `in`-Liste in der URL — deshalb in Haeppchen. */
const HAPPEN = 200;

/**
 * Die Projekte aus `projectIds`, ueber die nicht mehr gemeldet wird.
 *
 * Gibt im Fehlerfall eine LEERE Menge zurueck (= alles meldet weiter) und wirft
 * nie: diese Auskunft darf keine Ansicht zum Absturz bringen.
 */
export const ladeStilleProjekte = async (projectIds: string[]): Promise<Set<string>> => {
    const still = new Set<string>();
    const ids = [...new Set((projectIds || []).filter(Boolean))];
    if (ids.length === 0) return still;

    try {
        // (1) Welche davon liegen im Archiv? Nur fuer die lohnt der zweite
        //     Schritt — bei allen anderen aendert die Antwort nichts.
        const archiviert: string[] = [];
        for (let i = 0; i < ids.length; i += HAPPEN) {
            const { data, error } = await supabase
                .from('projects')
                .select('id, status')
                .in('id', ids.slice(i, i + HAPPEN))
                .eq('status', ABGESCHLOSSEN);
            if (error) throw error;
            for (const p of data || []) archiviert.push(p.id as string);
        }
        if (archiviert.length === 0) return still;

        // (2) Steht an einer Position noch etwas offen?
        //
        // Die Bestandteil-Spalten (Nr. 165/166) gehen mit, obwohl
        // `istAusgeglichen` heute nur die Mengen vergleicht: eine gebuchte,
        // aber gesperrte Paketposition (erbracht, nicht abgerechnet) haelt das
        // Projekt schon ueber die Mengen im Melden. Wer die Regel um das Gate
        // erweitert, findet es so bereits in der Hand. Fehlen die Spalten
        // (SQL nicht gefahren), scheitert die Abfrage mit 42703 — und dann
        // meldet alles weiter (Kopf: „die sichere Richtung"). Die Stunden-
        // Spalten (Nr. 171) gehen aus demselben Grund mit: jede Mengen-Liste
        // liest die ganze Paketposition (bestandteileSpaltenWaechter.test.ts).
        const jeProjekt = new Map<string, Array<{ quantityUsed: number; quantityBilled: number; quantityWaived: number }
            & Pick<ServiceItem, 'bestandteile' | 'bestandteileOffen'>>>();
        for (const id of archiviert) jeProjekt.set(id, []);
        for (let i = 0; i < archiviert.length; i += HAPPEN) {
            const { data, error } = await supabase
                .from('project_services')
                .select('project_id, quantity_used, quantity_billed, quantity_waived, bestandteile, bestandteile_offen, stunden_kontingent, stunden_erfasst')
                .in('project_id', archiviert.slice(i, i + HAPPEN));
            if (error) throw error;
            for (const s of data || []) {
                jeProjekt.get(s.project_id as string)?.push({
                    quantityUsed: Number(s.quantity_used) || 0,
                    quantityBilled: Number(s.quantity_billed) || 0,
                    quantityWaived: Number(s.quantity_waived) || 0,
                    ...bestandteilFelderAusZeile(s),
                });
            }
        }

        for (const [id, positionen] of jeProjekt) {
            if (istAusgeglichen(positionen)) still.add(id);
        }
        return still;
    } catch (e) {
        console.warn('Stillgelegte Projekte nicht ermittelbar — es wird weiter gemeldet:', e);
        return new Set<string>();
    }
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TOCHTER MIT TAGESPAUSCHALE — der Tag ist in der Mutter gebucht
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Warum das ueberhaupt beschafft werden muss: die Sicht fuehrt zwar
 * `parent_project_id` mit, aber weder den Abrechnungsmodus der Tochter noch
 * das Protokoll der Mutter. Beides steht im Projekt-JSON, und ohne beides
 * laesst sich die Frage „ist der Tag schon abgerechnet?" nicht beantworten.
 *
 * Geladen wird sparsam: nur die Toechter, die ueberhaupt gemeldet werden, und
 * von den Muettern nur der `serviceLog`-Pfad — nicht der ganze Blob.
 *
 * Wie ueberall hier fuehrt jeder Fehler zu MELDEN: faellt eine Abfrage aus,
 * kommt eine leere Menge zurueck und alles bleibt stehen.
 */

/** Schluessel einer Berichtszeile: Projekt und Nummer. */
export const berichtSchluessel = (projectId: string, inspectionNumber: number): string =>
    `${projectId}:${inspectionNumber}`;

export const ladeTagGedeckteBerichte = async (rows: UnbookedSentReportRow[]): Promise<Set<string>> => {
    const gedeckt = new Set<string>();
    const kinder = (rows || []).filter((r) => r.parent_project_id);
    if (kinder.length === 0) return gedeckt;

    try {
        // (1) Welche Toechter rechnen als Tagespauschale ab? Der Modus steht am
        //     KIND — bei Einzelabrechnung deckt derselbe Tag nichts.
        // Die GANZE Konfiguration, nicht nur der aktuelle Modus: seit die
        // Abrechnungsart umstellbar ist, gilt fuer jede Begehung die Art IHRES
        // Tages (`billingModeAt`).
        const kindIds = [...new Set(kinder.map((r) => r.project_id))];
        const konfiguration = new Map<string, LinkedProjectConfig | undefined>();
        for (let i = 0; i < kindIds.length; i += HAPPEN) {
            // Aus den SPALTEN (Nr. 71), nicht aus dem Blob: seit Nr. 71 schreibt
            // der Client `linkedProjectConfig` nicht mehr ins JSON. 6172-1 hatte
            // dort NULL, galt als „Art unbekannt" und blieb mit 61 Berichten
            // stehen, die die Mutter alle am selben Tag gebucht hatte (07.09.2026).
            const { data, error } = await supabase
                .from('projects')
                .select(`id, ${PROJECT_LINK_COLUMNS.join(', ')}, cfg:data->metadata->globalData->linkedProjectConfig`)
                .in('id', kindIds.slice(i, i + HAPPEN));
            if (error) throw error;
            for (const p of data || []) {
                const zeile = p as ProjectLinkColumns & { id: string; cfg?: LinkedProjectConfig | null };
                konfiguration.set(zeile.id, linkConfigFromRow(zeile, zeile.cfg ?? null));
            }
        }

        const betroffen = kinder.filter(
            (r) => billingModeAt(konfiguration.get(r.project_id), r.inspection_date) === TAGESPAUSCHALE,
        );
        if (betroffen.length === 0) return gedeckt;
        const mutterIds = [...new Set(betroffen.map((r) => r.parent_project_id as string))];

        // (2) Die Begehungspositionen der Muetter — gruppenweit, wie im
        //     Versandpfad: zwei Toechter koennen verschiedene Positionen
        //     treffen und sind trotzdem derselbe Tag.
        const inspektion = new Map<string, Set<string>>();
        for (const id of mutterIds) inspektion.set(id, new Set());
        for (let i = 0; i < mutterIds.length; i += HAPPEN) {
            const { data, error } = await supabase
                .from('project_services')
                .select('id, project_id, assignment')
                .in('project_id', mutterIds.slice(i, i + HAPPEN))
                .eq('assignment', 'inspection');
            if (error) throw error;
            for (const s of data || []) {
                inspektion.get(s.project_id as string)?.add(s.id as string);
            }
        }

        // (3) Die Protokolle der Muetter — nur dieser eine JSON-Pfad.
        const protokolle = new Map<string, BillingLogEntry[]>();
        for (let i = 0; i < mutterIds.length; i += HAPPEN) {
            const { data, error } = await supabase
                .from('projects')
                .select('id, log:data->metadata->globalData->serviceLog')
                .in('id', mutterIds.slice(i, i + HAPPEN));
            if (error) throw error;
            for (const p of data || []) {
                const log = (p as { log?: unknown }).log;
                protokolle.set((p as { id: string }).id, Array.isArray(log) ? (log as BillingLogEntry[]) : []);
            }
        }

        for (const r of betroffen) {
            const mutter = r.parent_project_id as string;
            if (istTagGedeckt(protokolle.get(mutter), inspektion.get(mutter) || new Set(), r.inspection_date)) {
                gedeckt.add(berichtSchluessel(r.project_id, r.inspection_number));
            }
        }
        return gedeckt;
    } catch (e) {
        console.warn('Tagespauschalen-Deckung nicht ermittelbar — es wird weiter gemeldet:', e);
        return new Set<string>();
    }
};
